import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './TreatsBanner.css';

const DISMISS_KEY = 'petlings_treats_banner_dismissed_at';
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

// Try to read recent grant state. The backend endpoint is documented in the
// May 2026 release plan but may not be live yet - we degrade gracefully and
// fall back to the "starter treats waiting" message for any logged-in user
// who has not actively dismissed the banner.
async function fetchGrantState() {
  try {
    const res = await fetch(
      (process.env.REACT_APP_API_URL || 'https://api.petlings.gg/api/v1') +
        '/shop/grants/recent',
      { credentials: 'include' }
    );
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

function isDismissed() {
  try {
    const stored = localStorage.getItem(DISMISS_KEY);
    if (!stored) return false;
    const at = Number(stored);
    if (!Number.isFinite(at)) return false;
    return Date.now() - at < DISMISS_TTL_MS;
  } catch {
    return false;
  }
}

function rememberDismiss() {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    /* private mode etc - ignore */
  }
}

export default function TreatsBanner() {
  const { isAuthenticated, user, loading } = useAuth();
  const location = useLocation();
  const [dismissed, setDismissed] = useState(() => isDismissed());
  const [grant, setGrant] = useState(null);

  // Don't show on the login page itself or in /auth/callback - banner would
  // be redundant noise.
  const onAuthRoute =
    location.pathname.startsWith('/login') ||
    location.pathname.startsWith('/auth/');

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    fetchGrantState().then((data) => {
      if (!cancelled) setGrant(data);
    });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user?.id]);

  if (loading || dismissed || onAuthRoute) return null;

  let kind = null;
  let body = null;
  let cta = null;
  let ctaTo = null;

  if (!isAuthenticated) {
    kind = 'signin';
    body = (
      <>
        <strong>Get free treats.</strong> Sign in once and we'll drop a starter
        pack into your wallet. Earn more during seasonal events.
      </>
    );
    // No CTA: the body already says "Sign in" and the page has a Sign in
    // button in the nav, so a third button would be redundant.
  } else if (grant?.active_event) {
    kind = 'event';
    body = (
      <>
        <strong>{grant.active_event.name}:</strong> claim your bonus{' '}
        {grant.active_event.amount} treats.
      </>
    );
    cta = 'Claim';
    ctaTo = '/shop';
  } else if (grant?.starter_unclaimed === true) {
    // Only show the starter copy when the backend confirms the user has
    // an unclaimed starter pack. The /shop/grants/recent endpoint isn't
    // live yet, so this branch stays dormant - better than showing a
    // misleading "claim now" button that does nothing.
    kind = 'starter';
    body = (
      <>
        <strong>Your starter treats are waiting.</strong> Earn more by playing -
        no purchase required.
      </>
    );
    cta = 'Open shop';
    ctaTo = '/shop';
  } else {
    // Logged in with nothing to surface - stay quiet.
    return null;
  }

  const handleDismiss = () => {
    rememberDismiss();
    setDismissed(true);
  };

  return (
    <div className={`treats-banner treats-banner-${kind}`} role="region" aria-label="Treats availability">
      <div className="container treats-banner-inner">
        <span className="treats-banner-icon" aria-hidden="true">🦴</span>
        <p className="treats-banner-text">{body}</p>
        {cta && ctaTo && (
          <Link to={ctaTo} className="treats-banner-cta">
            {cta} →
          </Link>
        )}
        <button
          type="button"
          className="treats-banner-dismiss"
          onClick={handleDismiss}
          aria-label="Dismiss"
        >
          ×
        </button>
      </div>
    </div>
  );
}
